import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod/v4";

import { AuthenticationError } from "@api/session";

import { login } from "@/actions/sessionActions";
import FlashMessagesList from "@/components/flash";
import { addDangerFlash, addWarningFlash } from "@/components/flash/flashReducer";
import RHInput from "@/containers/RHInput";
import handleError from "@/handleError";
import { useAppDispatch, useAppSelector } from "@/hooks";
import useTitle from "@/hooks/useTitle";
import { sessionSelector } from "@/store/sessionReducer";

const validationSchema = z.object({
  username: z.string().trim().nonempty({ message: "translation:signinform.username_missing" }),
  password: z.string().trim().nonempty({ message: "translation:signinform.password_missing" }),
  otp: z.string().optional(),
});

type ValuesType = z.infer<typeof validationSchema>;

const initialValues: ValuesType = {
  username: "",
  password: "",
  otp: "",
};

export default function SignInForm() {
  const session = useAppSelector(sessionSelector);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation"]);
  const [otpNeeded, setOtpNeeded] = useState(false);

  useTitle(t("translation:signinform.title"));

  useEffect(() => {
    if (session.isAuthenticated) {
      navigate("/");
    }
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ValuesType>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialValues,
    mode: "onBlur",
  });

  async function onSubmit(values: ValuesType) {
    try {
      await dispatch(login(values.username, values.password, values.otp || ""));
      navigate("/");
    } catch (e: any) {
      if (e.message === "OTP needed") {
        setOtpNeeded(true);

        dispatch(addWarningFlash(t("translation:signinform.otp_needed")));
        return;
      }

      if (e.message === "Too Many Requests") {
        dispatch(addDangerFlash(t("translation:signinform.too_many_requests")));
      } else {
        dispatch(addDangerFlash(t("translation:signinform.wrong_login_data"), 5000));
      }

      if (!(e instanceof AuthenticationError)) {
        handleError(e);
      }
    }
  }

  return (
    <div className={clsx("TT-login-form-container", { "otp-needed": otpNeeded })}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FlashMessagesList className="sign-in-form" />

        <label htmlFor="inputUsername" className="visually-hidden">
          {t("translation:signinform.username")}
        </label>
        <RHInput
          id="inputUsername"
          type="text"
          control={control}
          name="username"
          rules={{ required: true }}
          className="username"
          placeholder={t("translation:signinform.username")}
          autoFocus
          autoComplete="username"
          errorClassName="visually-hidden"
          namespace="translation"
        />

        <label htmlFor="inputPassword" className="visually-hidden">
          {t("translation:signinform.password")}
        </label>
        <RHInput
          className="password"
          type="password"
          id="inputPassword"
          control={control}
          name="password"
          rules={{ required: true }}
          placeholder={t("translation:signinform.password")}
          errorClassName="visually-hidden"
          namespace="translation"
        />

        {otpNeeded && (
          <>
            <label htmlFor="inputOtp">{t("translation:signinform.otpCode")}</label>
            <RHInput
              type="text"
              id="inputOtp"
              control={control}
              name="otp"
              rules={{ required: true }}
              placeholder={t("translation:signinform.otpCode")}
            />
          </>
        )}

        <div className="d-grid">
          <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
            {t("translation:signinform.login")}
          </Button>
        </div>
      </Form>
    </div>
  );
}
